import type { IdMatchType } from '../types/auth.types';

/** [0-9] rather than \d, matching the server-side rule exactly. */
const PROVIDER_ID_PATTERN = /^(?:[0-9]{9,10}|[0-9]{2}-[0-9]{7})$/;

/** Longest valid Provider ID (a 10-digit NPI, or a Tax ID written as 12-3456789). */
export const PROVIDER_ID_MAX_LENGTH = 10;

/**
 * A Provider ID is a 9-digit Tax ID (optionally hyphenated as 12-3456789) or a 10-digit NPI. The same
 * rule is enforced server-side — this check only spares the user a round trip, it never grants access.
 */
export function isValidProviderId(providerId: string): boolean {
  return PROVIDER_ID_PATTERN.test(providerId.trim());
}

/** An NPI is exactly 10 digits; a Tax ID is 9, written plain or hyphenated as 12-3456789. */
const NPI_PATTERN = /^[0-9]{10}$/;

/**
 * What to call the identifier the provider signed in with (PP-012).
 *
 * The two forms a Provider ID may take are not interchangeable to the person reading them: showing a
 * 10-digit NPI under the label "Provider ID" reads as a different number than the one they typed. The
 * value itself is never reformatted — only what it is called.
 *
 * `idMatchType` is what portal-backend says the directory actually matched, and it wins whenever it is
 * present. The digit count is the fallback, because the BF contract marks every provider field nullable
 * and none may be assumed present — and because a Tax ID stored with ten digits would otherwise be
 * called an NPI on the strength of its length alone.
 */
export function providerIdLabel(providerId: string, idMatchType?: IdMatchType | null): string {
  if (idMatchType === 'Npi') {
    return 'NPI';
  }

  if (idMatchType === 'TaxId') {
    return 'Provider ID';
  }

  return NPI_PATTERN.test(providerId.trim()) ? 'NPI' : 'Provider ID';
}
